import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowRight, Mail, Share2, Check } from 'lucide-react';
import {
  calculateKinseyScore,
  getInterpretation,
  getKinseyDefinition,
  getTimeInterpretation,
  getDirectionInsight,
  getConstraintInsight,
  getConfidenceText
} from '../../lib/kinsey-data';
import { useShare } from '../../lib/useShare';

const DualScaleBar = ({ pastScore, presentScore, gender }) => {
  const pastPct = pastScore !== null ? (pastScore / 6) * 100 : 0;
  const presentPct = presentScore !== null ? (presentScore / 6) * 100 : 0;
  const isNeutral = gender === 'non-binary' || gender === 'not-specified';

  return (
    <div className="space-y-6" data-testid="kinsey-scale-bar">
      {/* Scale labels */}
      <div className="flex justify-between text-xs text-muted-foreground">
        {[0, 1, 2, 3, 4, 5, 6].map(n => (
          <span key={n}>{n}</span>
        ))}
      </div>

      {/* Past */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground font-medium">Past</span>
          <span className="text-foreground font-medium">{pastScore !== null ? pastScore : '\u2014'}</span>
        </div>
        <div className="relative w-full h-2 bg-border/40 rounded-full">
          {pastScore !== null && (
            <>
              <div
                className="absolute left-0 top-0 h-2 bg-muted-foreground/30 rounded-full transition-all duration-700"
                style={{ width: `${pastPct}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-muted-foreground/50 rounded-full border-2 border-background transition-all duration-700"
                style={{ left: `calc(${pastPct}% - 6px)` }}
                data-testid="kinsey-past-marker"
              />
            </>
          )}
        </div>
      </div>

      {/* Present */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-primary font-medium">Present</span>
          <span className="text-foreground font-medium">{presentScore !== null ? presentScore : '\u2014'}</span>
        </div>
        <div className="relative w-full h-2 bg-border/40 rounded-full">
          {presentScore !== null && (
            <>
              <div
                className="absolute left-0 top-0 h-2 bg-primary rounded-full transition-all duration-700"
                style={{ width: `${presentPct}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full border-2 border-background shadow-sm transition-all duration-700"
                style={{ left: `calc(${presentPct}% - 8px)` }}
                data-testid="kinsey-present-marker"
              />
            </>
          )}
        </div>
      </div>

      {/* Axis labels - gender-relative */}
      <div className="flex justify-between text-[10px] text-muted-foreground/60">
        {isNeutral ? (
          <>
            <span>More attraction to women</span>
            <span>More attraction to men</span>
          </>
        ) : (
          <>
            <span>Opposite-sex attraction</span>
            <span>Same-sex attraction</span>
          </>
        )}
      </div>

      {/* Clarity note */}
      <div className="text-center space-y-1">
        <p className="text-[11px] text-muted-foreground/50">
          This reflects attraction pattern, not identity.
        </p>
        {isNeutral && (
          <p className="text-[11px] text-muted-foreground/50">
            The classic Kinsey scale was designed around male/female attraction categories, so this result is shown in the most neutral way possible.
          </p>
        )}
      </div>
    </div>
  );
};

export const KinseyResults = ({ answers, gender, onBackToHub, onGoToHAEVN }) => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const { sharing, createShare } = useShare();

  const result = useMemo(() => calculateKinseyScore(answers, gender), [answers, gender]);
  const interpretation = getTimeInterpretation(result.pastScore, result.presentScore, result.direction, gender);
  const directionInsight = getDirectionInsight(result);
  const constraintInsight = getConstraintInsight(result);
  const confidenceText = getConfidenceText(result.confidence);
  const presentDef = getKinseyDefinition(result.presentScore, gender);

  const handleEmailSubmit = async () => {
    if (!email.trim()) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'kinsey' })
      });
    } catch (e) {
      // Still show success to user even if backend fails
    }
    setEmailSent(true);
  };

  const handleShare = async () => {
    const url = await createShare('kinsey', { answers, gender, result });
    if (url) {
      navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    }
  };

  return (
    <section className="min-h-screen py-24 px-6" data-testid="kinsey-results-section">
      <div className="max-w-2xl mx-auto">
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
            Your Results
          </span>
          <h2 className="text-3xl sm:text-4xl font-light text-foreground" data-testid="kinsey-score-display">
            Your Attraction Pattern
          </h2>
        </motion.div>

        {/* Past → Present Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="border-border bg-card mb-8" data-testid="kinsey-scale-card">
            <CardContent className="py-8 space-y-6">
              {/* 1. Estimated Kinsey Score */}
              <div className="text-center" data-testid="kinsey-score-display-value">
                <span className="text-xs text-muted-foreground block mb-1">Estimated Kinsey Score</span>
                <span className="text-5xl font-light text-foreground">{result.presentScore !== null ? result.presentScore : '\u2014'}</span>
              </div>

              {/* 2. Closest Category + 3. Definition */}
              {presentDef && (
                <div className="text-center border-t border-border/30 pt-4" data-testid="kinsey-definition-block">
                  <span className="text-sm text-primary font-medium" data-testid="kinsey-category-label">Category {presentDef.category}</span>
                  <p className="text-base text-foreground/80 mt-1" data-testid="kinsey-definition-text">{presentDef.definition}</p>
                </div>
              )}

              {/* Visual scale */}
              <DualScaleBar
                pastScore={result.pastScore}
                presentScore={result.presentScore}
                gender={gender}
              />

              {/* 4. Past to Present */}
              <div className="flex items-center justify-center gap-6 border-t border-border/30 pt-4" data-testid="kinsey-past-present">
                <div className="text-center">
                  <span className="text-xs text-muted-foreground block mb-1">Past</span>
                  <span className="text-2xl font-light text-muted-foreground">{result.pastScore !== null ? result.pastScore : '\u2014'}</span>
                </div>
                <span className="text-xl text-muted-foreground/40 mt-3">{'\u2192'}</span>
                <div className="text-center">
                  <span className="text-xs text-primary block mb-1">Present</span>
                  <span className="text-2xl font-light text-foreground">{result.presentScore !== null ? result.presentScore : '\u2014'}</span>
                </div>
              </div>

              {/* 5. What this suggests */}
              <div className="border-t border-border/30 pt-4" data-testid="kinsey-suggestion">
                <span className="text-xs text-muted-foreground block mb-2">What this suggests</span>
                <p className="text-sm text-foreground/80 leading-relaxed">{interpretation}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* What May Be Shaping This */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <Card className="border-border bg-card mb-8" data-testid="kinsey-constraint-card">
            <CardHeader>
              <CardTitle className="text-xl font-light">What May Be Shaping This</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/80 leading-relaxed">{constraintInsight}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Direction Insight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Card className="border-border bg-card mb-8" data-testid="kinsey-direction-card">
            <CardHeader>
              <CardTitle className="text-xl font-light">Direction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  result.direction === 'stable'
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-primary/10 text-primary'
                }`} data-testid="kinsey-direction-label">
                  {result.directionLabel}
                </span>
              </div>
              <p className="text-foreground/80 leading-relaxed">{directionInsight}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Confidence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <Card className="border-border bg-card mb-8" data-testid="kinsey-confidence-card">
            <CardHeader>
              <CardTitle className="text-xl font-light">How Clear This Pattern Is</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  result.confidence === 'high'
                    ? 'bg-[hsl(var(--teal)/0.1)] text-[hsl(var(--teal))]'
                    : result.confidence === 'medium'
                    ? 'bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))]'
                    : 'bg-primary/10 text-primary'
                }`}>
                  {result.confidence === 'high' ? 'High' : result.confidence === 'medium' ? 'Medium' : 'Low'}
                </span>
              </div>
              <p className="text-foreground/80 leading-relaxed">{confidenceText}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Reality Check */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <Card className="border-primary/20 bg-primary/5 mb-10" data-testid="kinsey-reality-check">
            <CardContent className="py-6">
              <p className="text-foreground/80 leading-relaxed text-sm">
                This is not a label.
                It is a snapshot of how your attraction has shown up over time.
                Patterns are contextual and can continue to evolve.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Capture */}
        {showEmailCapture && !emailSent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <Card className="border-border bg-card mb-10" data-testid="kinsey-email-capture">
              <CardContent className="py-8">
                <h3 className="text-lg font-light text-foreground mb-2">
                  Want to save your results or get future tools?
                </h3>
                <div className="flex gap-3 mt-4">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                    data-testid="kinsey-email-input"
                  />
                  <Button
                    onClick={handleEmailSubmit}
                    className="rounded-full px-6"
                    disabled={!email.trim()}
                    data-testid="kinsey-email-submit"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send me my results
                  </Button>
                </div>
                <button
                  onClick={() => setShowEmailCapture(false)}
                  className="text-xs text-muted-foreground mt-3 hover:text-foreground transition-colors"
                  data-testid="kinsey-email-skip"
                >
                  Skip
                </button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {emailSent && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-10">
            <Card className="border-[hsl(var(--teal)/0.3)] bg-[hsl(var(--teal)/0.05)]">
              <CardContent className="py-4">
                <p className="text-sm text-[hsl(var(--teal))]">
                  Noted. We will send your results and future tools to {email}.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65 }}
          className="space-y-4"
        >
          <Button
            onClick={handleShare}
            variant="outline"
            className="w-full rounded-full py-6 text-base"
            disabled={sharing}
            data-testid="kinsey-share-btn"
          >
            {copiedLink ? (
              <><Check className="w-4 h-4 mr-2" />Link Copied!</>
            ) : sharing ? (
              <>Creating link...</>
            ) : (
              <><Share2 className="w-4 h-4 mr-2" />Share Results</>
            )}
          </Button>
          <Button
            onClick={onGoToHAEVN}
            className="w-full rounded-full py-6 text-base"
            data-testid="kinsey-go-to-haevn"
          >
            Take the Sexual Satisfaction Profile
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={onBackToHub}
            variant="ghost"
            className="w-full rounded-full"
            data-testid="kinsey-back-to-hub"
          >
            Back to Self Discovery Hub
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
